import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '../../components/Toast';
import { metaCookies, trackPixel } from '../../lib/metaPixel';

type OnboardingContext = {
  canExitOnboarding?: boolean;
  previousWorkspaceId?: string | null;
  createNew?: boolean;
};

export function OnboardingStep1() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, workspace, profile, fetchProfile, setProfile, setWorkspace } = useAuthStore();
  const toast = useToast();
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [onboardingContext, setOnboardingContext] = useState<OnboardingContext | null>(null);

  useEffect(() => {
    const rawContext = sessionStorage.getItem('lima_onboarding_context');
    if (!rawContext) {
      setOnboardingContext(null);
      return;
    }

    try {
      setOnboardingContext(JSON.parse(rawContext) as OnboardingContext);
    } catch {
      setOnboardingContext(null);
    }
  }, []);

  useEffect(() => {
    const metadataWebsite = typeof user?.user_metadata?.website === 'string'
      ? user.user_metadata.website.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '')
      : '';
    const storedWebsite = sessionStorage.getItem('lima_marketing_prefill_website')?.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '') || '';
    const marketingWebsite = metadataWebsite || storedWebsite;
    setUrl(!workspace?.id && marketingWebsite ? marketingWebsite : '');
  }, [workspace?.id, user?.user_metadata?.website]);

  const isCreatingWorkspace = onboardingContext?.createNew === true || !(workspace?.id || profile?.workspace_id);
  // The Trial is used up, so the new workspace goes straight to checkout.
  const needsSubscription = isCreatingWorkspace && !!profile?.trial_used_at;

  const createWorkspace = async (brandUrl: string) => {
    const { data, error } = await supabase.functions.invoke('create-workspace', {
      body: {
        name: needsSubscription ? name.trim() : 'My Workspace',
        brandUrl,
        ...metaCookies(),
      },
    });

    if (error) {
      const body = await error.context?.json?.().catch(() => null);
      if (body?.awaitingPaymentWorkspaceId) {
        // Take the user back to the workspace they still need to pay for.
        await supabase.from('profiles').update({ workspace_id: body.awaitingPaymentWorkspaceId }).eq('id', user!.id);
        await fetchProfile(user!.id);
        sessionStorage.removeItem('lima_onboarding_context');
        toast.error(body.error);
        navigate('/onboarding/payment', { replace: true });
        return;
      }
      throw new Error(body?.error || error.message);
    }

    if (!data?.workspaceId) {
      throw new Error('Failed to create workspace');
    }

    if (data.state === 'trial' && !useAuthStore.getState().viewingAs) trackPixel('StartTrial', {}, `start_trial_${data.workspaceId}`);
    sessionStorage.removeItem('lima_onboarding_context');
    queryClient.invalidateQueries({ queryKey: ['userWorkspaces'] });
    // Set the store and navigate together so the route guards see the new workspace.
    setWorkspace(data.workspace);
    setProfile(data.profile);
    navigate(data.state === 'awaiting_payment' ? '/onboarding/payment' : '/onboarding/step-2');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim() || !user) return;

    setIsSubmitting(true);
    try {
      let cleanUrl = url.trim().toLowerCase();
      cleanUrl = cleanUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '');

      if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(cleanUrl)) {
        toast.error('Please enter a valid domain (e.g. google.com)');
        setIsSubmitting(false);
        return;
      }

      if (isCreatingWorkspace) {
        await createWorkspace(cleanUrl);
        return;
      }

      // A workspace created blank before domains were set at creation sets its domain
      // here, once. After that it's fixed; support changes it.
      const { error } = await supabase
        .from('workspaces')
        .update({ brand_url: cleanUrl })
        .eq('id', (workspace?.id || profile?.workspace_id)!);

      if (error) throw error;

      await fetchProfile(user.id);

      sessionStorage.removeItem('lima_onboarding_context');
      navigate('/onboarding/step-2');
    } catch (error: any) {
      console.error('Error saving brand URL:', error);
      toast.error(error.message || 'Failed to save URL');
      setIsSubmitting(false);
    }
  };

  const handleExitOnboarding = async () => {
    if (!user || !profile?.workspace_id) return;

    setIsExiting(true);
    try {
      const targetWorkspaceId = onboardingContext?.previousWorkspaceId || profile.workspace_id;

      const { error } = await supabase
        .from('profiles')
        .update({
          workspace_id: targetWorkspaceId,
          onboarding_completed: true,
        })
        .eq('id', user.id);

      if (error) throw error;

      sessionStorage.removeItem('lima_onboarding_context');
      await fetchProfile(user.id);
      queryClient.invalidateQueries({ queryKey: ['userWorkspaces'] });
      navigate('/overview', { replace: true });
    } catch (error: any) {
      toast.error(error?.message || 'Failed to exit onboarding');
    } finally {
      setIsExiting(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="app-wrapper">
        <div id="no-saved-ads" className="onboarding-wrapper">
          <div className="intro-wrapper">
            <div className="onboarding-video-shell">
              <video
                className="onboarding-video"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                onLoadedMetadata={(event) => {
                  event.currentTarget.playbackRate = 1;
                }}
              >
                <source src="/images/lima-onboarding-video.webm" type="video/webm" />
              </video>
            </div>
            <div className="spacer-small"></div>
            <div className="text-size-large text-weight-semibold text-colour-dark-brown">
              {needsSubscription ? 'This workspace needs a Subscription' : "Welcome, let's find your competitors"}
            </div>
            <div className="spacer-xxtiny"></div>
            <div className="text-size-regular text-colour-alt-grey align-center">
              {needsSubscription
                ? "You've used your free Trial. Name the workspace and add its website, then choose a plan."
                : "Add your website below and we'll run a quick search"}
            </div>
            <div className="plus-btn"></div>
            <div className="spacer-medium"></div>
            <div className="generate-results-box">
              <div className="url-form-outer">
                <form
                  onSubmit={handleSubmit}
                  className="url-form"
                >
                  {needsSubscription && (
                    <>
                      <div className="spacer-xsmall"></div>
                      <label htmlFor="workspace-name" className="form_label has-left-spacing">
                        Workspace name
                      </label>
                      <input
                        className="form_input is-url-input w-input"
                        maxLength={100}
                        placeholder="e.g. Acme"
                        type="text"
                        id="workspace-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        disabled={isSubmitting || isExiting}
                        autoFocus
                      />
                    </>
                  )}
                  <div className="spacer-xsmall"></div>
                  <label htmlFor="brand-url" className="form_label has-left-spacing">
                    Your website URL
                  </label>
                  <input
                    className="form_input is-url-input w-input"
                    maxLength={256}
                    placeholder="e.g. google.com"
                    type="text"
                    id="brand-url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    required
                    disabled={isSubmitting || isExiting}
                    autoFocus={!needsSubscription}
                  />
                  <button
                    type="submit"
                    className="metal-button is-round is-colour is-floaty"
                    disabled={isSubmitting || isExiting || !url.trim() || (needsSubscription && !name.trim())}
                  >
                    <div className="embed-icon">
                      <svg width="14" height="14" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.707 0.292969L7.293 1.70697L13.586 7.99997H0V9.99997H13.586L7.293 16.293L8.707 17.707L17.414 8.99997L8.707 0.292969Z" fill="currentColor" />
                      </svg>
                    </div>
                    <div className="display-none">generate button</div>
                  </button>
                </form>
              </div>
            </div>
            {onboardingContext?.canExitOnboarding && (
              <>
                <div className="spacer-small"></div>
                <div className="align-content-middle">
                  <button
                    type="button"
                    className="link-style"
                    onClick={handleExitOnboarding}
                    disabled={isExiting || isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="gradient-style"></div>
      </div>
    </div>
  );
}
